/* ================================================================
 * Copyright © 2025 Tobeunicorn Co., Ltd. All rights reserved.
 *
 * 프로젝트명 : ArmyLink V1.0
 * 설명 : 홍보결과 목록 컴포넌트
 * 버전 : 1.0
 * 파일명 : PromoResults.tsx
 * 작성자 : 강동형
 * 작성일 : 2025-03-01
 * ================================================================ 
 * 수정 : 2025-07-16, 강동형 : 저작권 추가, 기획 변경에 따른 레이아웃 및 스타일 변경
 * 수정 : 2025-07-18, 강동형 : 리스트 배경색 추가
 *================================================================
 */

import React, { useEffect, useState, useRef, useCallback } from 'react'
import { Box, Typography, Menu, MenuItem, styled, Button } from '@mui/material';
import { promoResultType } from 'src/types/PromoType';
import { useDialogStore } from 'src/stores/DialogStore';
import PromoResultsList from './PromoResultsList';
import { PromoResultType } from 'src/pages/PromoResultPage';
import { submitPromoFilter } from './useSubmitPromoFilter';
import { Icon_ArrowDown } from '../common/CommonIcons';

interface Props{
    setTab : React.Dispatch<React.SetStateAction<PromoResultType>>;
}

const FilterContainer = styled('div')({
  margin : '0px -20px',
  padding: '0px 20px',
  paddingTop:'8px',
  zIndex:2,
  marginBottom:'16px'
})

// 필터 바 스타일링
const FilterBar = styled('div')({
  display: 'flex', 
  justifyContent: 'space-between', 
  alignItems: 'center', 
  });

// 필터 버튼 스타일링
const FilterButton = styled(Button)({ 
  all:'unset',
  display: 'flex', 
  alignItems: 'center', 
  marginLeft: 8, 
  color: 'black', 
  fontSize:'14px',
  backgroundColor:'#f4f4f4'
});

const ListWarpper = styled('div')({
  maxHeight: 'calc(100vh - 299.11px)',
  overflowY: 'auto',
  overflowX: 'hidden',
  backgroundColor:'#F4F4F4'
})

const PromoReults = ({setTab}:Props) => {
    const data = useDialogStore((state)=>state.response_data)
    const total_rows = useDialogStore((state)=>state.total_rows)
    const setField = useDialogStore((state)=>state.setField)
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [sortCriterion, setSortCriterion] = useState('최신순');
    const [render,setRender] = useState<promoResultType[]>([])

  useEffect(()=>{
    handleClose('visit_date','desc','최신순')
  },[])

  useEffect(() => {
    if(data){
      setRender(data);
    }
    }, [data]);

  const didMount = useRef(false);
  useEffect(() => {
    if (didMount.current) {
      (async () => {
        await submitPromoFilter();
      })();
    } else {
      didMount.current = true;
    }
  }, [sortCriterion]);


  // 필터 버튼 클릭 이벤트 핸들러
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
      setAnchorEl(event.currentTarget);
  };

  // 필터 메뉴 닫기 이벤트 핸들러
  const handleClose = (sort_by:string, sort_order:string, text:string) => {
    setField('sort_by',sort_by)
    setField('sort_order',sort_order)
    setSortCriterion(text)
  };

const loadMoreRef = useRef<HTMLDivElement | null>(null);
const containerRef = useRef<HTMLDivElement | null>(null);
const observerRef = useRef<IntersectionObserver | null>(null);
const [refReady, setRefReady] = useState(false);
    
const setLoadMoreRef = useCallback((node: HTMLDivElement | null) => {
  loadMoreRef.current = node;
  if (node && containerRef.current) setRefReady(true);
  else setRefReady(false);
}, []);

const setContainerRef = useCallback((node: HTMLDivElement | null) => {
  containerRef.current = node;
  if (node && loadMoreRef.current) setRefReady(true);
  else setRefReady(false);
}, []);

useEffect(() => {
  if (!refReady) {
    return;
  }

  const target = loadMoreRef.current;
  const root = containerRef.current;

  if (!target || !root) {
    return;
  }
  const observer = new IntersectionObserver(
    ([entry]) => {
      if (entry.isIntersecting) {
        (async () => {
        if (data.length !== 0) {
          await submitPromoFilter(false);
        } else {
          await submitPromoFilter(true);
        }
        })();
      }
    },
    {
      root: null,
      threshold: 0.1,
    }
  );

  observer.observe(target);
  observerRef.current = observer;

  return () => {
    observer.disconnect();
    setRefReady(false);
  };
}, [refReady]);

  return (
    <Box>
        <FilterContainer>
            <FilterBar>
                <Typography variant="body2" sx ={{color:'#7F7F89', fontSize:'12px', fontWeight:500}}>
                    총 <span style={{color:'black',fontSize:'16px',marginLeft:'5px'}}>{total_rows} </span>건
                </Typography>
                <div>
                <FilterButton onClick={handleClick} endIcon={<Icon_ArrowDown style={{fontSize:'14px',color:'#969696'}}/>}>
                    {sortCriterion}
                </FilterButton>
                <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
                    <MenuItem onClick={() => handleClose('visit_date','desc','최신순')}>최신순</MenuItem>
                    <MenuItem onClick={() => handleClose('visit_date','asc','과거순')}>과거순</MenuItem>
                    <MenuItem onClick={() => handleClose('school_name','asc','이름순')}>이름순</MenuItem>
                    <MenuItem onClick={() => handleClose('school_name','desc','이름역순')}>이름역순</MenuItem>
                </Menu>
                </div>
            </FilterBar>
        </FilterContainer>
        <ListWarpper ref={setContainerRef}>
            {render?.map((items, index) => (
              <PromoResultsList key={index} results={items} setTab ={setTab} isLast={render.length-1 === index}/>
            ))} 
        <div ref={setLoadMoreRef} style={{height:'1px'}}/>
        </ListWarpper>
    </Box>
  )
}

export default PromoReults